import { IHwTypeBase, Item, Subsystem } from '@dto/architecture.dto';
import { AxiosError } from 'axios';
import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppGlobStore } from 'src/appGlobalStore';
import { RoutePath } from 'src/data/Routes';
import { MenuNode } from 'src/model/MenuNode';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import { formatSearchQueryTitle } from '../domain/queryCodec';
import { useSearchStore } from '../store/useSearchStore';

import { SearchItemResponseDTO } from '@dto/searchResponse.dto';
import { Spin } from "antd";
import { sortCustomLinks } from 'src/content/tools/cmdb/customLinks/sortCustomLinks';
import { getApi } from 'src/util/apiCalls';

/** Fetch search results for the given API URL. */
const fetchSearchResults = async (
  apiUrl: string,
  signal?: AbortSignal
): Promise<SearchItemResponseDTO> => {
  const response = await getApi<SearchItemResponseDTO>(apiUrl, signal);
  return response.data;
};

type FilterFunc = (item: IHwTypeBase | Subsystem) => boolean;

export const getLabel = (
  key: keyof IHwTypeBase | keyof Subsystem,
  data: (IHwTypeBase | Subsystem)[]
): ((type: string, filter?: FilterFunc) => string) => {
  return (type: string, filter?: FilterFunc): string => {
    const filtered = filter ? data.filter(filter) : data;
    const idx = filtered.findIndex((ht) => (ht[key] as string) === type);

    if (idx === -1) {
      console.warn(`Add label for '${type}'`);
      return type;
    }

    return filtered[idx].label;
  };
};

const useSearchResults = (path: string, parm: string, api: string): [Item[], boolean] => {
  const [data, setData] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const hwTypes = useSearchStore(s => s.hwTypes);
  const subsystems = useSearchStore(s => s.subsystems);
  const availableArchitecture = useSearchStore(s => s.availableArchitecture);
  const isLabelDataLoading = useSearchStore(s => s.isLabelDataLoading);

  const changeTree = useCallback((nodes: MenuNode[] = []): void => {
    useAppGlobStore.setState((state) => ({
      leftMenu: { ...state.leftMenu, children: nodes, title: 'Search' },
    }));
    GLOB.refresh();
  }, []);

  useEffect(() => {
    useSearchStore.setState({ isSearchResultsLoading: loading });
    return () => useSearchStore.setState({ isSearchResultsLoading: false });
  }, [loading]);

  const labelFns = useMemo(() => {
    if (hwTypes.length === 0 || subsystems.length === 0) return null;
    return {
      hwTypeLabel: getLabel('hw_type', hwTypes),
      subsystemLabel: getLabel('subsystem', subsystems),
    };
  }, [hwTypes, subsystems]);

  useEffect(() => {
    if (path !== RoutePath.SEARCH) {
      setLoading(false);
      return () => changeTree([]);
    }

    if (!labelFns) {
      setLoading(isLabelDataLoading);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    changeTree([{ key: 'loading', selectable: false, isLeaf: true, title: createElement(Spin, { size: 'medium' }) }]);

    fetchSearchResults(api, controller.signal)
      .then((responseData) => {
        const items = responseData.data?.items ?? [];
        const { hwTypeLabel, subsystemLabel } = labelFns;
        const titleText = formatSearchQueryTitle(parm, availableArchitecture);

        changeTree(
          parm
            ? [
              { key: 'found', selectable: false, isLeaf: true, title: items ? `Found: ${items.length}` : 'Found: 0' },
              { key: 'search', selectable: false, isLeaf: true, title: titleText },
            ]
            : [{ key: 'found', selectable: false, isLeaf: true, title: items ? `Found: ${items.length}` : 'Found: 0' }]
        );

        setData(
          items.map((item) => {
            const filterSubs: FilterFunc = ({ hw_type }) => item.hw_type === hw_type;
            return {
              ...item,
              hw_type: hwTypeLabel(item.hw_type),
              subsystem: subsystemLabel(item.subsystem, filterSubs),
              custom_links: sortCustomLinks(item.custom_links ?? []),
            };
          })
        );
      })
      .catch((reason: AxiosError) => {
        changeTree([{ key: 'found', selectable: false, isLeaf: true, title: 'Found: 0' }]);
        Log.error('Search failed', reason);
      })
      .finally(() => setLoading(false));

    return () => {
      controller.abort();
      changeTree([]);
    };
  }, [path, parm, api, labelFns, isLabelDataLoading, availableArchitecture, changeTree]);

  return [data, loading];
};

export default useSearchResults;
